import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IMessage } from '../../services/messaging/IMessagingProvider';

interface Conversation {
  userId: string;
  lastMessage: IMessage | null;
  unreadCount: number;
}

interface MessagesState {
  conversations: Conversation[];
  currentConversation: string | null;
  messages: { [userId: string]: IMessage[] };
  lastFetchedAt: { [userId: string]: number }; // timestamp per conversation
  isConnected: boolean;
  onlineUsers: string[];
}

const initialState: MessagesState = {
  conversations: [],
  currentConversation: null,
  messages: {},
  lastFetchedAt: {},
  isConnected: false,
  onlineUsers: []
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    setConversations: (state, action: PayloadAction<Conversation[]>) => {
      state.conversations = action.payload;
    },
    setCurrentConversation: (state, action: PayloadAction<string | null>) => {
      state.currentConversation = action.payload;
    },
    setMessages: (state, action: PayloadAction<{ userId: string; messages: IMessage[]; updateFetchTime?: boolean }>) => {
      state.messages[action.payload.userId] = action.payload.messages;
      if (action.payload.updateFetchTime !== false) {
        state.lastFetchedAt[action.payload.userId] = Date.now();
      }
    },
    addMessage: (state, action: PayloadAction<IMessage & { currentUserId?: string }>) => {
      const msg = action.payload;

      // Determine the other user in the conversation
      // If currentUserId is passed, use it. Otherwise, we need to infer from existing state
      let conversationUserId: string;
      let currentUserId: string | undefined = msg.currentUserId;

      // Try to infer current user from existing conversations if not provided
      if (!currentUserId) {
        // Look at existing messages to figure out who the current user is
        const allMessages = Object.values(state.messages).flat();
        if (allMessages.length > 0) {
          // Find a message where we can determine the current user
          const existingMsg = allMessages[0];
          // The current user is the one who appears consistently as sender or recipient
          currentUserId = existingMsg.senderId;
        }
      }

      // Determine which user this conversation is with
      if (currentUserId) {
        conversationUserId = msg.senderId === currentUserId ? msg.recipientId : msg.senderId;
      } else {
        // Fallback: use recipientId (this might not be correct but prevents crash)
        conversationUserId = msg.recipientId;
      }

      // Add message to the conversation with the other user
      if (!state.messages[conversationUserId]) {
        state.messages[conversationUserId] = [];
      }

      // Upsert: if a message with this id is already present, only replace it
      // when the new copy is an UPGRADE (decrypted version replacing the
      // earlier ciphertext). Otherwise skip — protects against socket-replay
      // dupes from clobbering decrypted content with stale ciphertext.
      const existingIdx = state.messages[conversationUserId].findIndex(m => m.id === msg.id);
      if (existingIdx === -1) {
        state.messages[conversationUserId].push(msg);
      } else {
        const existing = state.messages[conversationUserId][existingIdx];
        if (existing.encrypted && !msg.encrypted) {
          state.messages[conversationUserId][existingIdx] = msg;
        }
      }

      // Update conversations
      const convIndex = state.conversations.findIndex(c => c.userId === conversationUserId);

      // Determine if this message should increment unread count
      // Only increment if:
      // 1. The message is FROM the other user (we received it)
      // 2. We're not currently viewing this conversation
      const isReceivedMessage = msg.senderId === conversationUserId;
      const isNotCurrentConversation = conversationUserId !== state.currentConversation;
      const shouldIncrementUnread = isReceivedMessage && isNotCurrentConversation;

      if (convIndex !== -1) {
        state.conversations[convIndex].lastMessage = msg;
        if (shouldIncrementUnread) {
          state.conversations[convIndex].unreadCount++;
        }
      } else {
        // New conversation - set unread count to 1 if this is a received message
        state.conversations.push({
          userId: conversationUserId,
          lastMessage: msg,
          unreadCount: shouldIncrementUnread ? 1 : 0
        });
      }
    },
    setUserOnline: (state, action: PayloadAction<string>) => {
      if (!state.onlineUsers.includes(action.payload)) {
        state.onlineUsers.push(action.payload);
      }
    },
    setUserOffline: (state, action: PayloadAction<string>) => {
      state.onlineUsers = state.onlineUsers.filter(id => id !== action.payload);
    },
    markConversationAsRead: (state, action: PayloadAction<string>) => {
      const convIndex = state.conversations.findIndex(c => c.userId === action.payload);
      if (convIndex !== -1) {
        state.conversations[convIndex].unreadCount = 0;
      }
    }
  }
});

export const {
  setConnected,
  setConversations,
  setCurrentConversation,
  setMessages,
  addMessage,
  setUserOnline,
  setUserOffline,
  markConversationAsRead
} = messagesSlice.actions;

// How long cached messages are considered fresh before a refetch (ms)
export const MESSAGE_CACHE_TTL = 3 * 60 * 1000; // 3 minutes

export default messagesSlice.reducer;
